import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Activity, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { email, password, name };
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success(isLogin ? "Access granted" : "Account created");

      // Seed data on first login
      try {
        await api.post("/seed");
      } catch (_) {}

      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#09090b] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md">
        {/* Terminal header */}
        <div className="border border-zinc-800 bg-[#0a0a0c]">
          <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#ff3b30]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#eab308]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#00ff41]" />
            </div>
            <span className="text-[10px] text-zinc-500 font-mono tracking-wider uppercase">
              uwatu:~/{isLogin ? "login" : "register"}
            </span>
          </div>

          <div className="p-8">
            {/* Logo */}
            <div className="flex items-center gap-4 mb-8">
              <img src="/uwatu-logo.png" alt="UWATU" className="w-16 h-16 object-contain" />
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-wider text-zinc-100">
                  UWATU
                </h1>
                <p className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-mono">
                  Commodities Intelligence
                </p>
                <p className="text-[8px] text-[#00ff41]/60 uppercase tracking-wider font-mono mt-0.5">
                  AI-Driven Decisioning
                </p>
              </div>
            </div>

            {/* Boot sequence text */}
            <div className="mb-6 font-mono text-[11px] space-y-1">
              <p className="text-zinc-500">$ initializing market intelligence engine...</p>
              <p className="text-[#00ff41]">$ soft commodities | hard commodities | ready</p>
              <p className="text-zinc-500">
                $ {isLogin ? "authenticate to proceed" : "create operator account"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                    Operator Name
                  </label>
                  <Input
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="full name"
                    className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600"
                    required
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                  Email Address
                </label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="operator@uwatu.ai"
                  className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Input
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    className="rounded-none border-zinc-800 bg-black font-mono text-sm focus:ring-1 focus:ring-[#00ff41] focus:border-[#00ff41] text-zinc-100 placeholder:text-zinc-600 pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    onClick={() => setShowPw(!showPw)}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full rounded-none bg-[#00ff41] text-black hover:bg-[#00cc33] font-mono text-xs uppercase tracking-wider h-10 transition-colors duration-100"
              >
                {loading ? "Processing..." : isLogin ? "Authenticate" : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                data-testid="toggle-auth-mode-btn"
                className="text-[11px] text-zinc-500 hover:text-[#00ff41] font-mono transition-colors duration-100"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? "$ create new operator account" : "$ login to existing account"}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-800 px-4 py-2 flex items-center justify-between">
            <span className="text-[9px] text-zinc-600 font-mono">DATA</span>
            <span className="text-[9px] text-zinc-600 font-mono">INSIGHTS</span>
            <span className="text-[9px] text-zinc-600 font-mono">PRICING</span>
            <span className="text-[9px] text-zinc-600 font-mono">RISK</span>
            <span className="text-[9px] text-[#00ff41] font-mono">CAPITAL</span>
          </div>
        </div>
      </div>
    </div>
  );
}
